import React, { useState, useEffect } from 'react';
import { User, Car, Trash2 } from 'lucide-react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { getVehicleModels } from '../../../lib/api';

// Vehicle Info Form Component for each vehicle
function VehicleInfoForm({ vehicle, index, vehicleYears, vehicleMakes, vehicleColors, onUpdate }) {
  const [models, setModels] = useState([]);
  
  useEffect(() => {
    if (vehicle.vehicleInfo.make) {
      getVehicleModels(vehicle.vehicleInfo.make).then(r => {
        setModels(r.data.models || []);
      }).catch(() => setModels([]));
    } else {
      setModels([]);
    }
  }, [vehicle.vehicleInfo.make]);

  const vehicleIcons = {
    'sedan': '/vehicle-sedan.png',
    '2_row_suv': '/vehicle-2row-suv.png',
    '3_row_suv': '/vehicle-3row-suv.png',
    'minivan': '/vehicle-minivan.png'
  };

  return (
    <div className="mb-6 border border-slate-200 rounded-xl p-5 bg-slate-50">
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
        <img 
          src={vehicleIcons[vehicle.vehicleSize]} 
          alt={vehicle.vehicleSizeLabel} 
          className="w-10 h-10 object-contain"
        />
        <div>
          <h3 className="font-semibold text-slate-900">Vehicle {index + 1}: {vehicle.vehicleSizeLabel}</h3>
          <p className="text-sm text-slate-500">{vehicle.serviceName} - ${vehicle.price}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-700 font-medium text-sm">Year</Label>
          <Select
            value={vehicle.vehicleInfo.year}
            onValueChange={(value) => onUpdate('year', value)}
          >
            <SelectTrigger className="mt-1 h-10">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {vehicleYears.map((year) => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-slate-700 font-medium text-sm">Make</Label>
          <Select
            value={vehicle.vehicleInfo.make}
            onValueChange={(value) => {
              onUpdate('make', value);
              onUpdate('model', '');
            }}
          >
            <SelectTrigger className="mt-1 h-10">
              <SelectValue placeholder="Select make" />
            </SelectTrigger>
            <SelectContent>
              {vehicleMakes.map((make) => (
                <SelectItem key={make} value={make}>{make}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-slate-700 font-medium text-sm">Model</Label>
          <Select
            value={vehicle.vehicleInfo.model}
            onValueChange={(value) => onUpdate('model', value)}
            disabled={!vehicle.vehicleInfo.make}
          >
            <SelectTrigger className="mt-1 h-10">
              <SelectValue placeholder={vehicle.vehicleInfo.make ? "Select model" : "Select make first"} />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model} value={model}>{model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-slate-700 font-medium text-sm">Color</Label>
          <Select
            value={vehicle.vehicleInfo.color}
            onValueChange={(value) => onUpdate('color', value)}
          >
            <SelectTrigger className="mt-1 h-10">
              <SelectValue placeholder="Select color" />
            </SelectTrigger>
            <SelectContent>
              {vehicleColors.map((color) => (
                <SelectItem key={color} value={color}>{color}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function DetailsStep({
  customerInfo,
  setCustomerInfo,
  vehicles,
  vehicleYears,
  vehicleMakes,
  vehicleColors,
  updateVehicleInfo
}) {
  return (
    <div className="animate-fade-in" data-testid="cleaning-step-2">
      <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">Your Details & Vehicle</h2>
      <p className="text-slate-500 mb-6">Tell us about yourself and your vehicle(s)</p>
      
      <div className="space-y-6">
        {/* Customer Info Section */}
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="full_name" className="text-slate-700 font-medium">Full Name</Label>
              <Input
                id="full_name"
                value={customerInfo.full_name}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Enter your full name"
                className="mt-1"
                data-testid="cleaning-name-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(123) 456-7890"
                  className="mt-1"
                  data-testid="cleaning-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@example.com"
                  className="mt-1"
                  data-testid="cleaning-email-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Info Section */}
        <div>
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 px-1">
            <Car className="w-5 h-5 text-emerald-600" />
            Vehicle Details
          </h3>
          {vehicles.map((vehicle, idx) => (
            <VehicleInfoForm
              key={vehicle.id}
              vehicle={vehicle}
              index={idx}
              vehicleYears={vehicleYears}
              vehicleMakes={vehicleMakes}
              vehicleColors={vehicleColors}
              onUpdate={(field, value) => updateVehicleInfo(vehicle.id, field, value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
